import React from "react";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  Skeleton,
  Switch,
  TextField,
  Typography,
  alpha,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { Dayjs } from "dayjs";
import RadioCardGroup, { RadioCardOption } from "../../../Components/RadioCardGroup";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { acceptCurrencyKeystroke } from "./studentBillingValidation";

/**
 * Reusable "Billing Information" section for the Student Create and Edit
 * pages. All state is controlled — the parent form owns the values and
 * passes an onChange callback. This keeps the section consistent with the
 * rest of the numbered section pattern used on both pages.
 *
 * Behaviour:
 *  - Two radio cards (Monthly / Class-Wise). Value drives which extra
 *    fields render underneath.
 *  - Monthly: Monthly Amount (required, >= 0), Effective Date (required),
 *    Tax toggle.
 *  - Class-Wise: hides Monthly Amount, keeps Effective Date, shows an
 *    informational box.
 *  - Edit mode: renders a "Currently on file" chip so admin sees the
 *    baseline before making changes.
 *
 * Never calls any API itself — parent supplies loading, current arrangement
 * and validation errors.
 */

export type BillingTypeV2 = "monthly" | "class_wise";

export interface StudentBillingValues {
  billing_type_v2: BillingTypeV2 | "";
  monthly_amount: number | string | null;
  billing_effective_from: string | null; // YYYY-MM-DD
  tax_applicable: boolean;
}

export interface StudentBillingCurrent {
  billing_type: BillingTypeV2 | null;
  monthly_amount: number | null;
  tax_applicable: boolean;
  effective_from: string | null;
}

export interface StudentBillingErrors {
  billing_type_v2?: string;
  monthly_amount?: string;
  billing_effective_from?: string;
}

export interface StudentBillingTypeSectionProps {
  mode: "create" | "edit";
  values: StudentBillingValues;
  errors?: StudentBillingErrors;
  touched?: Partial<Record<keyof StudentBillingValues, boolean>>;
  onChange: (patch: Partial<StudentBillingValues>) => void;
  onBlur?: (field: keyof StudentBillingValues) => void;

  // Edit mode: the currently-active arrangement from the backend so the
  // admin sees "currently on file" before changing anything. Ignored on
  // Create.
  current?: StudentBillingCurrent | null;
  currentLoading?: boolean;
  currentError?: string | null;

  disabled?: boolean;
}

const BILLING_TYPE_OPTIONS: RadioCardOption[] = [
  {
    value: "monthly",
    title: "Monthly Billing",
    description:
      "Charge one fixed amount per billing period. Assigned class fees will not be billed separately.",
  },
  {
    value: "class_wise",
    title: "Class-Wise Billing",
    description: "Charge the fee configured on each assigned class.",
  },
];

const FieldLabel: React.FC<{ label: string; required?: boolean }> = ({
  label,
  required,
}) => (
  <Typography
    sx={{
      fontSize: 13,
      fontWeight: 500,
      color: "text.secondary",
      mb: 0.75,
      letterSpacing: "-0.01em",
    }}
  >
    {label}
    {required && (
      <Typography component="span" sx={{ color: "error.main", ml: 0.25 }}>
        *
      </Typography>
    )}
  </Typography>
);

const formatCurrency = (v: number | null | undefined): string =>
  v === null || v === undefined || Number.isNaN(v) ? "—" : `$${Number(v).toFixed(2)}`;

const StudentBillingTypeSection: React.FC<StudentBillingTypeSectionProps> = ({
  mode,
  values,
  errors,
  touched,
  onChange,
  onBlur,
  current,
  currentLoading,
  currentError,
  disabled,
}) => {
  const type = values.billing_type_v2;
  const isMonthly = type === "monthly";
  const isClassWise = type === "class_wise";
  const effectiveFromDayjs: Dayjs | null = values.billing_effective_from
    ? dayjs(values.billing_effective_from)
    : null;

  const showAmountError =
    !!errors?.monthly_amount && (touched?.monthly_amount || false);
  const showTypeError =
    !!errors?.billing_type_v2 && (touched?.billing_type_v2 || false);
  const showDateError =
    !!errors?.billing_effective_from && (touched?.billing_effective_from || false);

  return (
    <Box>
      {/* Current arrangement (Edit mode only) */}
      {mode === "edit" && (
        <Box sx={{ mb: 2.5 }}>
          {currentLoading ? (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
              <CircularProgress size={16} />
              <Skeleton variant="text" width={280} />
            </Box>
          ) : currentError ? (
            <Alert severity="error" sx={{ mb: 1 }}>
              Could not load current billing arrangement: {currentError}
            </Alert>
          ) : current && current.billing_type ? (
            <Box
              sx={{
                borderRadius: 2,
                border: (t) => `1px dashed ${t.palette.divider}`,
                p: 1.5,
                bgcolor: (t) => alpha(t.palette.info.main, 0.04),
                display: "flex",
                alignItems: "center",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <InfoOutlinedIcon fontSize="small" color="info" />
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                Currently on file:
              </Typography>
              <Chip
                size="small"
                color={current.billing_type === "monthly" ? "primary" : "secondary"}
                label={current.billing_type === "monthly" ? "Monthly Billing" : "Class-Wise Billing"}
              />
              {current.billing_type === "monthly" && (
                <Chip
                  size="small"
                  variant="outlined"
                  label={`Monthly Amount: ${formatCurrency(current.monthly_amount)}`}
                />
              )}
              <Chip
                size="small"
                variant="outlined"
                label={`Effective ${current.effective_from ?? "—"}`}
              />
              <Chip
                size="small"
                variant="outlined"
                label={current.tax_applicable ? "Tax Applicable" : "Tax Exempt"}
              />
            </Box>
          ) : (
            <Alert severity="info" sx={{ mb: 1 }}>
              This student does not yet have a billing arrangement.
            </Alert>
          )}
        </Box>
      )}

      {/* Billing Type radio cards */}
      <Box sx={{ mb: 2 }}>
        <FieldLabel label="Billing Type" required />
        <RadioCardGroup
          options={BILLING_TYPE_OPTIONS}
          value={type || ""}
          onChange={(v) => {
            // Switching to Class-Wise clears the amount string (null on the
            // wire) so a stale value can never be posted back. Switching
            // back to Monthly re-shows the field — the amount is
            // required-and-positive again.
            onChange({
              billing_type_v2: v as BillingTypeV2,
              monthly_amount: v === "class_wise" ? null : values.monthly_amount,
            });
            onBlur?.("billing_type_v2");
          }}
          error={showTypeError}
          disabled={disabled}
        />
        {showTypeError && (
          <Typography sx={{ color: "error.main", fontSize: 12, mt: 0.5, ml: 0.5 }}>
            {errors!.billing_type_v2}
          </Typography>
        )}
      </Box>

      {/* Conditional fields */}
      {type && (
        <Grid container spacing={2.5}>
          {isMonthly && (
            <Grid item xs={12} md={4}>
              <FieldLabel label="Monthly Amount" required />
              <TextField
                type="number"
                size="small"
                fullWidth
                disabled={disabled}
                name="monthly_amount"
                inputProps={{
                  min: "0.01",
                  step: "0.01",
                  inputMode: "decimal",
                  // Reject letters and multiple decimals at the browser
                  // level. Formik still runs Number() validation on submit.
                  pattern: "^\\d+(\\.\\d{1,2})?$",
                }}
                InputProps={{
                  // $ is a purely visual adornment — MUI never puts it
                  // inside the underlying <input>'s .value, so Formik
                  // stores a clean numeric string like "5" or "300.00".
                  startAdornment: (
                    <InputAdornment position="start">$</InputAdornment>
                  ),
                }}
                value={values.monthly_amount ?? ""}
                onChange={(e) => {
                  // Preserve the raw string so mid-typing states like
                  // "5." don't get coerced away. Number() runs only at
                  // validation and payload-normalisation time.
                  const next = acceptCurrencyKeystroke(e.target.value);
                  if (next === null) return; // rejected — keep previous value
                  onChange({ monthly_amount: next });
                }}
                onBlur={() => onBlur?.("monthly_amount")}
                error={showAmountError}
                helperText={
                  showAmountError
                    ? errors!.monthly_amount
                    : "Charged for every future billing period."
                }
              />
            </Grid>
          )}

          <Grid item xs={12} md={4}>
            <FieldLabel label="Billing Effective Date" required />
            <LocalizationProvider dateAdapter={AdapterDayjs}>
              <DatePicker
                value={effectiveFromDayjs}
                disabled={disabled}
                onChange={(v: Dayjs | null) => {
                  onChange({
                    billing_effective_from: v ? v.format("YYYY-MM-DD") : null,
                  });
                  onBlur?.("billing_effective_from");
                }}
                slotProps={{
                  textField: {
                    size: "small",
                    fullWidth: true,
                    error: showDateError,
                    helperText: showDateError
                      ? errors!.billing_effective_from
                      : "Applies to future invoices only.",
                  },
                }}
              />
            </LocalizationProvider>
          </Grid>

          <Grid item xs={12} md={4}>
            <FieldLabel label="Tax Applicable" />
            <Box
              sx={{
                border: (t) => `1px solid ${t.palette.divider}`,
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                bgcolor: values.tax_applicable
                  ? (t) => alpha(t.palette.success.main, 0.06)
                  : "transparent",
              }}
            >
              <Typography variant="body2" sx={{ color: "text.secondary" }}>
                {values.tax_applicable ? "Tax will be added" : "No tax"}
              </Typography>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    disabled={disabled}
                    checked={!!values.tax_applicable}
                    onChange={(_, checked) =>
                      onChange({ tax_applicable: checked })
                    }
                  />
                }
                label=""
              />
            </Box>
          </Grid>

          {isClassWise && (
            <Grid item xs={12}>
              <Alert
                severity="info"
                icon={<ReceiptLongOutlinedIcon />}
                sx={{ mt: 0.5 }}
              >
                Class fees will be taken automatically from each assigned class.
                You will not need to enter the fee again during class assignment.
              </Alert>
            </Grid>
          )}

          {isMonthly && (
            <Grid item xs={12}>
              <Alert severity="warning" sx={{ mt: 0.5 }}>
                Monthly billing overrides every assigned class fee. Class-level
                fees will still be shown for reference but they will not be
                billed separately.
              </Alert>
            </Grid>
          )}
        </Grid>
      )}
    </Box>
  );
};

export default StudentBillingTypeSection;
